<template>
  <div class="plan-form-container">
    <v-card flat>
      <v-card-text class="pa-6">
        <v-form ref="planForm" v-model="isValid">
          <v-row>
            <!-- Sección: Información Principal -->
            <v-col cols="12">
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                <span class="text-h6 font-weight-medium">Información del Plan</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field v-model="planData.nm" label="Nombre del Plan" placeholder="Ej. Plan Premium" outlined dense
                prepend-inner-icon="mdi-format-title" class="mb-2"
                :rules="[v => !!v || 'El nombre es requerido']"></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field v-model.number="planData.pc.C" label="Precio del Plan" placeholder="0.00" outlined dense
                prepend-inner-icon="mdi-currency-usd" type="number" min="0" step="0.01"
                :rules="[v => v !== '' && v !== null && v >= 0 || 'El precio es requerido']"></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field v-model.number="duracionCantidad" label="Duración" type="number" min="1" outlined dense
                prepend-inner-icon="mdi-clock-outline" :rules="[v => !!v || 'Requerido']"></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <v-select v-model="duracionUnidad" :items="unidadesDuracion" label="Unidad" outlined dense
                :rules="[v => !!v || 'Requerido']"></v-select>
            </v-col>

            <!-- Sección: Beneficios -->
            <v-col cols="12" class="mt-0">
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" class="mr-2">mdi-gift-outline</v-icon>
                <span class="text-h6 font-weight-medium">Beneficios del Plan</span>
                <v-spacer></v-spacer>
                <v-btn color="primary" small depressed rounded class="text-none" @click="addBeneficio">
                  <v-icon left small>mdi-plus</v-icon>
                  Agregar Beneficio
                </v-btn>
              </div>
            </v-col>

            <!-- Lista de Beneficios -->
            <v-col cols="12" v-if="planData.bf.length === 0">
              <v-alert type="info" text dense class="mb-4">
                No hay beneficios configurados. Presiona "Agregar Beneficio" para comenzar.
              </v-alert>
            </v-col>

            <v-col cols="12" v-for="(beneficio, bIdx) in planData.bf" :key="'bf-' + bIdx">
              <v-card outlined class="beneficio-card mb-4 rounded-lg">
                <v-card-title class="py-3 px-4 beneficio-header d-flex align-center">
                  <v-icon color="amber darken-2" class="mr-2" small>mdi-star</v-icon>
                  <span class="text-subtitle-1 font-weight-bold">Beneficio {{ bIdx + 1 }}</span>
                  <v-chip small color="primary" outlined class="ml-3">
                    Dinero Electrónico
                  </v-chip>
                  <v-spacer></v-spacer>
                  <v-btn icon small color="error" @click="removeBeneficio(bIdx)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text class="pa-4">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="beneficio.nm" label="Nombre del Beneficio"
                        placeholder="Ej. Crédito Restaurante" outlined dense prepend-inner-icon="mdi-label-outline"
                        :rules="[v => !!v || 'El nombre del beneficio es requerido']"></v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field v-model.number="beneficio.cf.cn" label="Cantidad" placeholder="0" outlined dense
                        type="number" min="0"
                        :rules="[v => v !== '' && v !== null && v >= 0 || 'La cantidad es requerida']"></v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-select v-model="beneficio.tp" :items="tiposBeneficio" label="Tipo" outlined dense
                        prepend-inner-icon="mdi-shape-outline" disabled></v-select>
                    </v-col>

                    <!-- Sección: Límites -->
                    <v-col cols="12">
                      <div class="d-flex align-center mb-3 mt-2">
                        <v-icon color="secondary" class="mr-2" small>mdi-speedometer</v-icon>
                        <span class="text-subtitle-2 font-weight-medium">Límites del Beneficio</span>
                        <v-spacer></v-spacer>
                        <v-btn x-small color="secondary" depressed rounded class="text-none" @click="addLimit(bIdx)">
                          <v-icon left x-small>mdi-plus</v-icon>
                          Agregar Límite
                        </v-btn>
                      </div>

                      <v-simple-table v-if="beneficio.lm && beneficio.lm.length > 0" dense
                        class="precios-table rounded-lg mb-4">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Periodo</th>
                              <th class="text-left">Tipo</th>
                              <th class="text-left">Cantidad</th>
                              <th class="text-center" width="60">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(limit, lIdx) in beneficio.lm" :key="'lm-' + bIdx + '-' + lIdx">
                              <td>
                                <v-select v-model="limit.pd" :items="periodosLimit" dense hide-details outlined
                                  class="my-1"></v-select>
                              </td>
                              <td>
                                <v-select v-model="limit.tp" :items="tiposLimit" dense hide-details outlined
                                  class="my-1"></v-select>
                              </td>
                              <td>
                                <v-text-field v-model.number="limit.cn" dense hide-details outlined type="number"
                                  min="1" class="my-1"></v-text-field>
                              </td>
                              <td class="text-center">
                                <v-btn icon x-small color="error" @click="removeLimit(bIdx, lIdx)">
                                  <v-icon x-small>mdi-close</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-col>

                    <!-- Fechas de expiración -->


                    <!-- Sección: Precios / Filtros del beneficio -->
                    <v-col cols="12">
                      <div class="d-flex align-center mb-3 mt-2">
                        <v-icon color="secondary" class="mr-2" small>mdi-filter-variant</v-icon>
                        <span class="text-subtitle-2 font-weight-medium">Items y precios</span>
                        <v-spacer></v-spacer>
                        <v-btn x-small color="secondary" depressed rounded class="text-none" @click="addPrecio(bIdx)">
                          <v-icon left x-small>mdi-plus</v-icon>
                          Agregar Item
                        </v-btn>
                      </div>

                      <v-simple-table v-if="beneficio.cf.pr.length > 0" dense class="precios-table rounded-lg">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Tipo</th>
                              <th class="text-left">Item</th>
                              <th class="text-left">PU</th>
                              <th class="text-left">a pagar</th>
                              <th class="text-left">a seleccionar</th>
                              <th class="text-center" width="60">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(precio, pIdx) in beneficio.cf.pr" :key="'pr-' + bIdx + '-' + pIdx">
                              <td style="min-width: 150px;">
                                <v-select v-model="precio.tp" :items="tiposFiltro" dense hide-details outlined
                                  class="my-1" style="max-width: 180px;"></v-select>
                              </td>
                              <td style="min-width: 200px;">
                                <v-autocomplete v-if="precio.tp != 'T'" hide-details v-model="precio.fl"
                                  :items="(precio.tp == 'P') ? dataItems.productos : dataItems.categorias"
                                  label="Beneficio" outlined dense required></v-autocomplete>
                                <v-text-field v-else hide-details v-model="precio.fl" label="Tag" outlined dense
                                  required></v-text-field>
                              </td>
                              <td style="min-width: 150px;">
                                <v-text-field :value="precio.pu ? precio.pu.C : null"
                                  @input="v => { if (!precio.pu) $set(precio, 'pu', { M: 'MXN', C: null }); precio.pu.C = (v === null || v === '') ? null : parseFloat(v) }"
                                  dense hide-details outlined placeholder="Opcional" type="number" min="0" step="0.01"
                                  prepend-inner-icon="mdi-currency-usd" class="my-1" :clearable="true"></v-text-field>
                              </td>
                              <td style="min-width: 150px;">
                                <v-text-field v-model.number="precio.cp" dense hide-details outlined
                                  placeholder="Cantidad a pagar" type="number" min="0" step="0.01" class="my-1"
                                  :clearable="true"></v-text-field>
                              </td>
                              <td style="min-width: 150px;">
                                <v-text-field v-model.number="precio.cs" dense hide-details outlined
                                  placeholder="Cantidad a seleccionar" type="number" min="0" step="0.01" class="my-1"
                                  :clearable="true"></v-text-field>
                              </td>

                              <td class="text-center">
                                <v-btn icon x-small color="error" @click="removePrecio(bIdx, pIdx)">
                                  <v-icon x-small>mdi-close</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>

                      <v-alert v-else type="warning" text dense class="mt-2">
                        No hay items configurados para este beneficio.
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 sticky-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('toggle', false)" class="text-none">
          Cancelar
        </v-btn>
        <v-btn color="primary" @click="save" :disabled="!isValid" elevation="0" class="px-8 text-none">
          Guardar Plan
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "PlanesForm",
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    dataEdit: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      Categories: [],
      isValid: true,
      duracionCantidad: 1,
      duracionUnidad: "M",
      planData: {
        id: null,
        nm: "",
        pc: { M: "MXN", C: 0 },
        bf: [],
      },
      tiposBeneficio: [
        { text: "Paquetes", value: "PQT" },
      ],
      tiposFiltro: [
        { text: "Producto", value: "P" },
        { text: "Categoría", value: "C" },
        { text: "Tags", value: "T" },
      ],
      periodosLimit: [
        { text: "Diario", value: "D" },
        { text: "Mensual", value: "M" },
        { text: "Anual", value: "A" },
      ],
      tiposLimit: [
        { text: "Usuario", value: "U" },
        { text: "Membresia", value: "M" },
      ],
      unidadesDuracion: [
        { text: "Días", value: "D" },
        { text: "Meses", value: "M" },
        { text: "Años", value: "Y" },
      ],
    };
  },
  computed: {
    ...mapGetters("Categorias", ["getCategoriesFiltered"]),
    clientes() {
      let clientes = [];
      let rowclientes = this.$store.getters.getClientes || [];
      rowclientes.forEach((element) => {
        if (element.id !== "PUBLICOGENERAL") {
          clientes.push({
            ...element,
            value: element.id,
            text: `${element.Apellidos || ""} ${element.Nombres || ""}${element.Apodo ? ` (${element.Apodo})` : ""}`
          });
        }
      });
      return clientes;
    },
    dataItems() {
      let productos = [];
      let categorias = [];
      let rowproductos = this.$store.getters.getProductos || [];

      rowproductos.forEach((element) => {
        if (element.Dlat == null) {
          productos.push({
            ...element,
            value: element.id,
            text: element.Nombre + " - " + element.Tipo
          });
        }
      });


      this.Categories.forEach((categories) => {
        categorias.push({
          ...categories,
          value: categories.id,
          text: categories.Nombre
        });
      });

      return { productos, categorias };
    }



  },
  mounted() {
    this.getCategories();
    if (this.edit && this.dataEdit) {
      let pcObj = { M: "MXN", C: 0 };
      if (typeof this.dataEdit.pc === 'object' && this.dataEdit.pc !== null) {
        pcObj = { ...this.dataEdit.pc };
      } else if (typeof this.dataEdit.pc === 'number') {
        pcObj.C = this.dataEdit.pc;
      }

      if (this.dataEdit.dr) {
        const match = this.dataEdit.dr.match(/^P(\d+)([DMY])$/);
        if (match) {
          this.duracionCantidad = parseInt(match[1]);
          this.duracionUnidad = match[2];
        } else {
          // Fallback
          this.duracionCantidad = 1;
          this.duracionUnidad = "M";
        }
      }

      const processPr = (pr) => (pr || []).map(p => {
        let puObj = { M: "MXN", C: null };
        if (typeof p.pu === 'object' && p.pu !== null) {
          puObj = { ...p.pu };
        } else if (typeof p.pu === 'number') {
          puObj.C = p.pu;
        }

        return {
          tp: p.tp || "P",
          fl: p.fl || "",
          pu: puObj,
          cp: p.cp ?? 1,
          cs: p.cs ?? 1,
        };
      });

      this.planData = {
        id: this.dataEdit.Id,
        nm: this.dataEdit.nm || "",
        pc: pcObj,
        bf: (this.dataEdit.bf || []).map((b) => {
          let cfObj = { cn: 0, pr: [] };
          if (b.cf) {
            cfObj.cn = b.cf.cn || 0;
            cfObj.pr = processPr(b.cf.pr);
          } else {
            cfObj.cn = b.cn || 0;
            cfObj.pr = processPr(b.pr);
          }
          return {
            ...b,
            lm: b.lm ? b.lm.map(l => ({ ...l })) : [],
            cf: cfObj,
            _menuInicio: false,
            _menuFin: false,
            ex: { ...b.ex },
          };
        }),
      };
    }
  },
  methods: {
    addBeneficio() {
      this.planData.bf.push({
        tp: "PQT",
        nm: "",
        lm: [],
        cf: {
          cn: 0,
          pr: [],
        },
        ex: { i: "", f: "" },
        _menuInicio: false,
        _menuFin: false,
      });
    },
    removeBeneficio(index) {
      this.planData.bf.splice(index, 1);
    },
    addPrecio(beneficioIndex) {
      if (!this.planData.bf[beneficioIndex].cf) {
        this.$set(this.planData.bf[beneficioIndex], 'cf', { cn: 0, pr: [] });
      }
      this.planData.bf[beneficioIndex].cf.pr.push({
        tp: "P",
        fl: "",
        pu: { M: "MXN", C: null },
        cp: 1,
        cs: 1,
      });
    },
    removePrecio(beneficioIndex, precioIndex) {
      this.planData.bf[beneficioIndex].cf.pr.splice(precioIndex, 1);
    },
    addLimit(beneficioIndex) {
      if (!this.planData.bf[beneficioIndex].lm) {
        this.$set(this.planData.bf[beneficioIndex], 'lm', []);
      }
      this.planData.bf[beneficioIndex].lm.push({
        pd: "D",
        tp: "U",
        cn: 1,
      });
    },
    removeLimit(beneficioIndex, limitIndex) {
      this.planData.bf[beneficioIndex].lm.splice(limitIndex, 1);
    },
    ...mapActions("Categorias", { fetchCategories: "getCategories" }),
    async getCategories() {
      await this.fetchCategories();
      const claims = this.$route.params.user?.tokenResult?.claims;
      this.Categories = this.getCategoriesFiltered(claims);
    },
    save() {
      if (this.$refs.planForm.validate()) {
        const mode = this.edit ? "EDIT" : "CREATE";

        // Clean internal properties before emitting
        const cleanBf = this.planData.bf.map((b) => {
          const { _menuInicio, _menuFin, ...cleanBeneficio } = b;

          // Process price items to handle null pu
          if (cleanBeneficio.cf && cleanBeneficio.cf.pr) {
            cleanBeneficio.cf.pr = cleanBeneficio.cf.pr.map(p => {
              const updatedP = { ...p };
              if (updatedP.pu && (updatedP.pu.C === null || updatedP.pu.C === "")) {
                updatedP.pu = null;
              }
              return updatedP;
            });
          }

          return cleanBeneficio;
        });

        const payload = {
          ...this.planData,
          dr: `P${this.duracionCantidad}${this.duracionUnidad}`,
          bf: cleanBf,
        };

        if (mode === "CREATE") {
          payload.id = null;
        }

        this.$emit("successData", payload, mode);
      }
    },
  },
};
</script>

<style scoped>
.plan-form-container {
  background-color: #fcfcfc;
}

.beneficio-card {
  border: 1px solid #e0e0e0 !important;
  transition: all 0.3s ease;
}

.beneficio-card:hover {
  border-color: var(--v-primary-lighten2) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.beneficio-header {
  background-color: #fafafa;
}

.precios-table {
  border: 1px solid #e8e8e8;
  overflow: hidden;
}

.precios-table thead th {
  background-color: #f5f7fa !important;
  color: #64748b !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.precios-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.sticky-actions {
  position: sticky;
  bottom: 0px;
  background-color: white !important;
  z-index: 10;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
